import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, History, FolderPlus, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, btnCls } from "./crud";
import LegalDocEditor from "./LegalDocEditor";

export default function LegalAdmin() {
  const [cats, setCats] = useState([]);
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [catEditing, setCatEditing] = useState(null);
  const [versions, setVersions] = useState(null);

  const load = () => {
    api.get("/admin/content/legal_categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/legal/documents").then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveCat = async () => {
    try {
      const { _id, created_at, updated_at, ...data } = catEditing;
      if (_id) await api.put(`/admin/content/legal_categories/${_id}`, { data });
      else await api.post("/admin/content/legal_categories", { data });
      toast.success("Categoría guardada"); setCatEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeCat = async (id) => {
    if (!window.confirm("¿Eliminar categoría?")) return;
    await api.delete(`/admin/content/legal_categories/${id}`);
    load();
  };

  const togglePublish = async (d) => {
    try {
      await api.put(`/admin/legal/documents/${d._id}`, { data: { published: !d.published } });
      toast.success(d.published ? "Documento ocultado de la web" : "Documento publicado en la web");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("¿Eliminar documento y todo su historial?")) return;
    await api.delete(`/admin/legal/documents/${id}`);
    toast.success("Eliminado"); load();
  };

  const showVersions = async (doc) => {
    const r = await api.get(`/admin/legal/documents/${doc._id}/versions`);
    setVersions({ doc, list: r.data });
  };

  const blankDoc = () => ({ slug: "", title: { es: "", en: "" }, content: { es: "", en: "" }, mode: "html", chapters: [], category_id: cats[0]?._id || "", order: docs.length, published: false, robots: "noindex,follow" });

  return (
    <div data-testid="legal-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-xl font-light">Área Legal — Repositorio Maestro</h1>
          <p className="font-mono-tech text-[10px] tracking-widest text-slate-400 mt-1">FUENTE ÚNICA DE VERDAD · VERSIONES · SNAPSHOTS · HASH SHA-256</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setCatEditing({ name: { es: "", en: "" }, order: cats.length })} className="inline-flex items-center gap-2 border border-slate-300 rounded-md text-slate-600 font-mono-tech text-xs tracking-widest px-4 py-2 hover:border-[#5698C7] hover:text-[#5698C7] transition-colors duration-200" data-testid="legal-add-category-btn">
            <FolderPlus size={14} /> CATEGORÍA
          </button>
          <button onClick={() => setEditing(blankDoc())} className={btnCls} data-testid="legal-add-doc-btn"><Plus size={14} /> DOCUMENTO</button>
        </div>
      </div>

      {cats.map((cat) => (
        <div key={cat._id} className="mb-8" data-testid={`legal-admin-cat-${cat._id}`}>
          <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-3">
            <span className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500">{(cat.name?.es || "").toUpperCase()}</span>
            <div className="flex gap-3">
              <button onClick={() => setCatEditing(cat)} className="text-slate-500 hover:text-[#5698C7]" data-testid={`legal-cat-edit-${cat._id}`}><Pencil size={12} /></button>
              <button onClick={() => removeCat(cat._id)} className="text-slate-500 hover:text-red-600" data-testid={`legal-cat-delete-${cat._id}`}><Trash2 size={12} /></button>
            </div>
          </div>
          <div className="border border-slate-200 rounded-lg bg-white shadow-sm">
            {docs.filter((d) => d.category_id === cat._id).map((d) => (
              <div key={d._id} className="border-b border-slate-100 last:border-0 p-4 flex items-center justify-between gap-4" data-testid={`legal-doc-row-${d._id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#0B1220] truncate">{d.title?.es}</span>
                    <span className={`shrink-0 border rounded-full px-2 py-0.5 text-[10px] font-medium ${d.published ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid={`legal-doc-pubstate-${d._id}`}>
                      {d.published ? "PUBLICADO" : "NO PUBLICADO"}
                    </span>
                  </div>
                  <div className="font-mono-tech text-[10px] text-slate-400 truncate">
                    /{d.slug} · v{d.version} · {d.mode === "clauses" ? "CLÁUSULAS" : "HTML"} · {d.hash?.slice(0, 16)}…
                    {d.draft && <span className="text-amber-600"> · ● BORRADOR EN CURSO</span>}
                  </div>
                </div>
                <div className="flex gap-3 shrink-0">
                  <button onClick={() => togglePublish(d)} className={d.published ? "text-emerald-600 hover:text-amber-600" : "text-slate-400 hover:text-emerald-600"} title={d.published ? "Ocultar de la web" : "Publicar en la web"} data-testid={`legal-doc-publish-toggle-${d._id}`}>
                    {d.published ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button onClick={() => showVersions(d)} className="text-slate-500 hover:text-[#5698C7]" title="Historial" data-testid={`legal-doc-history-${d._id}`}><History size={14} /></button>
                  <button onClick={() => setEditing(d)} className="text-slate-500 hover:text-[#5698C7]" data-testid={`legal-doc-edit-${d._id}`}><Pencil size={14} /></button>
                  <button onClick={() => removeDoc(d._id)} className="text-slate-500 hover:text-red-600" data-testid={`legal-doc-delete-${d._id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {docs.filter((d) => d.category_id === cat._id).length === 0 && <p className="p-4 text-xs text-slate-400">Sin documentos.</p>}
          </div>
        </div>
      ))}

      {catEditing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6" data-testid="legal-cat-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-sm">Categoría legal</h3>
              <button onClick={() => setCatEditing(null)}><X size={16} className="text-slate-500" /></button>
            </div>
            <div className="space-y-4">
              <input className={inputCls} placeholder="Nombre (ES)" value={catEditing.name?.es || ""} onChange={(e) => setCatEditing({ ...catEditing, name: { ...catEditing.name, es: e.target.value } })} data-testid="legal-cat-name-es" />
              <input className={inputCls} placeholder="Nombre (EN)" value={catEditing.name?.en || ""} onChange={(e) => setCatEditing({ ...catEditing, name: { ...catEditing.name, en: e.target.value } })} data-testid="legal-cat-name-en" />
              <input className={inputCls} type="number" placeholder="Orden" value={catEditing.order} onChange={(e) => setCatEditing({ ...catEditing, order: Number(e.target.value) })} data-testid="legal-cat-order" />
            </div>
            <div className="mt-6 flex justify-end"><button onClick={saveCat} className={btnCls} data-testid="legal-cat-save-btn">GUARDAR</button></div>
          </div>
        </div>
      )}

      {editing && (
        <LegalDocEditor doc={editing} cats={cats}
          onClose={() => { setEditing(null); load(); }}
          onSaved={(close) => { load(); if (close) setEditing(null); }} />
      )}

      {versions && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[80vh] overflow-auto p-6" data-testid="legal-versions-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-sm">Historial — {versions.doc.title?.es} (actual: v{versions.doc.version})</h3>
              <button onClick={() => setVersions(null)} data-testid="legal-versions-close"><X size={16} className="text-slate-500" /></button>
            </div>
            {versions.list.length === 0 && <p className="text-xs text-slate-400">Sin versiones anteriores. Los snapshots se generan al modificar el contenido.</p>}
            <div className="space-y-3">
              {versions.list.map((v) => (
                <div key={v._id} className="border border-slate-200 rounded-md p-4" data-testid={`legal-version-${v._id}`}>
                  <div className="font-mono-tech text-[10px] text-slate-500 tracking-widest">
                    v{v.version} · {v.snapshot_at?.slice(0, 19).replace("T", " ")} · {v.author}
                  </div>
                  <div className="font-mono-tech text-[10px] text-slate-400 break-all mt-1">HASH: {v.hash}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
